import React from 'react';

import { useIntl } from '../../../util/reactIntl';
import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import selectCss from '../SelectMultipleFilter/SelectMultipleFilter.module.css';

import SellerUsernameCombobox from './SellerUsernameCombobox';
import {
  SPONSORED_PARAM,
  RATED_PARAM,
  SELLER_PARAM,
  SELLER_TYPE_FIELD,
  SELLER_TYPE_VENDOR,
  SELLER_TYPE_HOBBYIST,
  SELLER_TYPE_SPONSORED,
  RATED_FIELD,
  RATED_VALUE,
  isParamActive,
  isSellerFilterActive,
  classifySellerType,
  toUrlParams,
  clearUrlParams,
} from './sellersFilterHelpers';

export { SPONSORED_PARAM, RATED_PARAM, SELLER_PARAM } from './sellersFilterHelpers';

/**
 * Combined seller username typeahead + seller-type / rated checkboxes.
 */
const SellersFilter = props => {
  const intl = useIntl();
  const {
    className,
    rootClassName,
    id,
    containerId,
    urlQueryParams = {},
    activeSeller = null,
    sellerNotFound = false,
    onSubmit,
    getAriaLabel,
  } = props;

  const sellerType = classifySellerType(urlQueryParams[SPONSORED_PARAM]);
  const ratedSelected = isParamActive(urlQueryParams[RATED_PARAM]);
  const sellerSelected = isSellerFilterActive(urlQueryParams[SELLER_PARAM]);
  const isSelected = !!sellerType || ratedSelected || sellerSelected;
  const selectedCount = [!!sellerType, ratedSelected, sellerSelected].filter(Boolean).length;

  const label = intl.formatMessage({ id: 'SellersFilter.label' });

  const initialValues = {
    [SELLER_TYPE_FIELD]: sellerType ? [sellerType] : [],
    [RATED_FIELD]: ratedSelected ? [RATED_VALUE] : [],
  };

  const handleSubmit = values => {
    if (values === null) {
      onSubmit(clearUrlParams());
      return;
    }
    onSubmit(toUrlParams(values, { previousSellerType: sellerType }));
  };

  const sellerOptions = [
    {
      field: SELLER_TYPE_FIELD,
      option: SELLER_TYPE_VENDOR,
      labelId: 'SellersFilter.allVendorListingsLabel',
      fieldIdSuffix: 'vendor',
    },
    {
      field: SELLER_TYPE_FIELD,
      option: SELLER_TYPE_HOBBYIST,
      labelId: 'SellersFilter.hobbyistListingsLabel',
      fieldIdSuffix: 'hobbyist',
    },
    {
      field: SELLER_TYPE_FIELD,
      option: SELLER_TYPE_SPONSORED,
      labelId: 'SellersFilter.sponsoredSellersLabel',
      fieldIdSuffix: 'sponsored',
    },
    {
      field: RATED_FIELD,
      option: RATED_VALUE,
      labelId: 'SellersFilter.ratedSellersLabel',
      fieldIdSuffix: 'rated',
    },
  ];

  return (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      ariaLabel={getAriaLabel(label, selectedCount > 0 ? String(selectedCount) : '')}
      isSelected={isSelected}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={initialValues}
      containerId={containerId}
    >
      <SellerUsernameCombobox
        id={`${id}-username`}
        urlQueryParams={urlQueryParams}
        activeSeller={activeSeller}
        sellerNotFound={sellerNotFound}
        onSubmit={onSubmit}
      />
      <fieldset className={selectCss.fieldGroupPlain}>
        <legend className={selectCss.accessibilityLegend}>{label}</legend>
        <ul className={selectCss.list}>
          {sellerOptions.map(({ field, option, labelId, fieldIdSuffix }) => (
            <li key={fieldIdSuffix} className={selectCss.item}>
              <FieldCheckbox
                id={`${id}-${fieldIdSuffix}`}
                name={field}
                label={intl.formatMessage({ id: labelId })}
                value={option}
                compact
                rootClassName={selectCss.compactCheckbox}
                labelClassName={selectCss.plainLabel}
                textClassName={selectCss.plainLabelText}
              />
            </li>
          ))}
        </ul>
      </fieldset>
    </FilterPlain>
  );
};

export default SellersFilter;
